import { useCallback, useEffect, useState } from "react";
import {
  Box, Stack, Typography, Paper, Chip, Alert, CircularProgress, IconButton, Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { BRAND } from "../../styles/theme";
import { getSaldos, parseMonto, fmtMonto, type CuentaSaldo } from "../../services/cmf";
import { useCmfAccounts } from "../../services/cmfAccounts";

function monedaChip(m: string) {
  const isUsd = m.toUpperCase().includes("DÓL") || m.toUpperCase().includes("DOL") || m.toUpperCase().includes("USD");
  return (
    <Chip
      label={isUsd ? "USD" : "ARS"}
      size="small"
      sx={{ fontWeight: 700, fontSize: 11, height: 22, color: BRAND.primary, bgcolor: BRAND.primaryLight }}
    />
  );
}

export default function Saldos() {
  const [cuentas, setCuentas] = useState<CuentaSaldo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  // Etiquetas operativas de las cuentas (editables desde Movimientos).
  const { label: cmfAccountLabel } = useCmfAccounts();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setCuentas(await getSaldos());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5">Saldos</Typography>
        <Tooltip title="Refrescar">
          <IconButton onClick={load} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {cuentas.length === 0 && !error && (
            <Typography color="text.secondary">No hay cuentas para mostrar.</Typography>
          )}
          {cuentas.map((c) => (
            <Paper key={c.numero_cuenta + c.moneda} sx={{ p: 2.5, borderRadius: 3, minWidth: 300, flex: "1 1 300px", maxWidth: 420 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontWeight: 700 }}>
                  Cuenta {c.numero_cuenta}
                  {cmfAccountLabel(c.numero_cuenta) && (
                    <Typography component="span" sx={{ ml: 1, fontSize: 12, color: "text.secondary", fontWeight: 500 }}>
                      {cmfAccountLabel(c.numero_cuenta)}
                    </Typography>
                  )}
                </Typography>
                {monedaChip(c.moneda)}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "ui-monospace, monospace" }}>
                CBU {c.cbu}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                {c.tipo_cuenta}
              </Typography>
              <Stack spacing={0.5}>
                <Row label="Disponible" value={fmtMonto(parseMonto(c.saldos?.disponible))} strong />
                <Row label="Real"       value={fmtMonto(parseMonto(c.saldos?.real))} />
                <Row label="Apertura"   value={fmtMonto(parseMonto(c.saldos?.apertura))} muted />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function Row({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography sx={{ fontSize: 13, color: muted ? "text.disabled" : "text.secondary" }}>{label}</Typography>
      <Typography sx={{ fontSize: strong ? 18 : 14, fontWeight: strong ? 800 : 600, color: strong ? BRAND.primary : muted ? "text.disabled" : "text.primary" }}>
        $ {value}
      </Typography>
    </Stack>
  );
}
